import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RecordPaymentModal - Reusable payment recording modal
 * Uses gold theme from BaseModal system
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import BaseModal, { Input as BaseInput, FormGroup as BaseFormGroup, FormLabel, PrimaryButton, SecondaryButton, } from './shared/BaseModal';
import mockDataService from '../services/mockDataService';
import { notify } from '../utils/notifications';
import apiClient from '../utils/apiClient';
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;
const FormGroup = styled(BaseFormGroup) `
  margin-bottom: 0;
`;
const Input = styled(BaseInput) `
  padding: 12px 14px;
`;
const Select = styled.select `
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;
const InfoLabel = styled.span `
  font-size: 14px;
  color: var(--text-tertiary);
`;
const InfoValue = styled.span `
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
`;
export const RecordPaymentModal = ({ isOpen, onClose, purchase, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        amount: 0,
        paymentMethod: 'wire_transfer',
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paidInFull: false,
        notes: ''
    });
    useEffect(() => {
        if (purchase && isOpen) {
            setForm(prev => ({
                ...prev,
                amount: purchase.amountOwed,
                paidInFull: false
            }));
        }
    }, [purchase, isOpen]);
    const handleSubmit = async () => {
        if (!purchase)
            return;
        setLoading(true);
        try {
            if (mockDataService.isMockMode()) {
                // Mock mode - create payment in mock service
                mockDataService.createPayment({
                    type: 'supplier-payment',
                    paymentNumber: `SPAY-${Date.now()}`,
                    supplierId: purchase.supplierId || 'SUP000001',
                    supplierCode: purchase.supplierCode || 'SUP',
                    supplierName: purchase.supplierName,
                    paymentFor: 'purchase',
                    referenceType: 'purchase',
                    referenceId: purchase.id,
                    referenceNumber: purchase.purchaseNumber,
                    amount: form.amount,
                    paymentDate: form.paymentDate,
                    paymentMethod: form.paymentMethod,
                    paymentReference: form.paymentReference,
                    status: 'cleared',
                    paidInFull: form.paidInFull || form.amount >= purchase.amountOwed,
                    notes: form.notes,
                    createdBy: 'MOCK_USER'
                });
                notify.success('Payment Recorded', `$${form.amount.toLocaleString()} payment recorded successfully`);
                onSuccess?.();
                onClose();
            }
            else {
                // Real API call
                const response = await apiClient.post('supplier-payments/create', {
                    purchaseId: purchase.id,
                    ...form
                });
                if (response.ok && response.data?.success) {
                    notify.success('Payment Recorded', `$${form.amount.toLocaleString()} payment recorded successfully`);
                    onSuccess?.();
                    onClose();
                }
                else {
                    notify.error('Failed', response.error || 'Failed to record payment');
                }
            }
        }
        catch (error) {
            console.error('Payment recording failed:', error);
            notify.error('Error', 'Failed to record payment');
        }
        finally {
            setLoading(false);
        }
    };
    const handleClose = () => {
        setForm({
            amount: 0,
            paymentMethod: 'wire_transfer',
            paymentReference: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paidInFull: false,
            notes: ''
        });
        onClose();
    };
    if (!purchase)
        return null;
    return (_jsxs(BaseModal, { isOpen: isOpen, onClose: handleClose, title: "Record Supplier Payment", titleAccent: "Payment", size: "md", footer: _jsxs(_Fragment, { children: [_jsx(SecondaryButton, { onClick: handleClose, children: "Cancel" }), _jsx(PrimaryButton, { onClick: handleSubmit, disabled: loading || !form.amount || !form.paymentReference, children: loading ? 'Recording...' : 'Record Payment' })] }), children: [_jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Purchase:" }), _jsx(InfoValue, { children: purchase.purchaseNumber })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Supplier:" }), _jsx(InfoValue, { children: purchase.supplierName })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Total:" }), _jsxs(InfoValue, { style: { color: 'var(--accent-gold)' }, children: ["$", purchase.total.toLocaleString()] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Already Paid:" }), _jsxs(InfoValue, { style: { color: '#15803D' }, children: ["$", purchase.amountPaid.toLocaleString()] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Amount Owed:" }), _jsxs(InfoValue, { style: { color: '#B91C1C', fontWeight: 700 }, children: ["$", purchase.amountOwed.toLocaleString()] })] })] }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Payment Amount *" }), _jsx(Input, { type: "number", step: "0.01", value: form.amount, onChange: (e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 }), placeholder: "Enter payment amount" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Payment Method *" }), _jsxs(Select, { value: form.paymentMethod, onChange: (e) => setForm({ ...form, paymentMethod: e.target.value }), children: [_jsx("option", { value: "wire_transfer", children: "Wire Transfer" }), _jsx("option", { value: "check", children: "Check" }), _jsx("option", { value: "ach", children: "ACH" }), _jsx("option", { value: "credit_card", children: "Credit Card" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Reference # *" }), _jsx(Input, { type: "text", value: form.paymentReference, onChange: (e) => setForm({ ...form, paymentReference: e.target.value }), placeholder: "Check #, wire confirmation, etc." })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Payment Date" }), _jsx(Input, { type: "date", value: form.paymentDate, onChange: (e) => setForm({ ...form, paymentDate: e.target.value }) })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Notes (Optional)" }), _jsx(Input, { type: "text", value: form.notes, onChange: (e) => setForm({ ...form, notes: e.target.value }), placeholder: "Additional notes..." })] })] })] }));
};
